import clsx from 'clsx';
import React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { LoadingIndicator } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useCreateInOwner } from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiClient } from '../../../state';
import type { SpaceCreationOnCreateSpace } from '../../Space/creation/types';
import { QuickActionCard, QuickActionCardHeading } from './QuickActionCard';

interface GitSyncCardProps {
    organizationId: string;
    onCreateSpace?: SpaceCreationOnCreateSpace;
}

/**
 * Card to start the Git Sync setup flow by creating a new space.
 * Used on the home screen and in the space creation dialog.
 */
export function GitSyncCard(props: GitSyncCardProps): React.ReactElement | null {
    const { organizationId, onCreateSpace } = props;
    const { navigate } = useNavigation();
    const { createSpace } = useCreateInOwner({ organization: organizationId });
    const [loading, withLoading] = useLoadingState(false);

    const handlePress = React.useCallback(() => {
        if (loading) {
            return;
        }

        return withLoading(async () => {
            if (onCreateSpace) {
                await onCreateSpace(
                    async ({ collectionId }) => {
                        const { data: space } = await apiClient.spaces.createSpace(organizationId, {
                            parent: collectionId,
                        });
                        analytics.logEvent(
                            analytics.createSpaceEvent(space, {
                                type: 'space_create',
                                source: 'gitsync',
                            })
                        );
                        return space;
                    },
                    (space) => {
                        navigate({
                            screen: {
                                type: 'space',
                                organization: organizationId,
                                space: space.id,
                                sideSheet: {
                                    type: 'gitsync',
                                    tab: 'setup',
                                },
                            },
                        });
                    }
                );
                return;
            }

            if (!createSpace) {
                return;
            }

            const space = await createSpace({
                source: 'gitsync',
                withNavigate: false,
            });

            navigate({
                screen: {
                    type: 'space',
                    organization: organizationId,
                    space: space.id,
                    sideSheet: {
                        type: 'gitsync',
                        tab: 'setup',
                    },
                },
            });
        });
    }, [onCreateSpace, organizationId, createSpace, navigate, withLoading, loading]);

    if (!onCreateSpace && !createSpace) {
        return null;
    }

    return (
        <QuickActionCard disabled={loading} onPress={handlePress} className="h-full">
            {loading ? (
                <LoadingIndicator size="small" className="p-2" />
            ) : (
                <div className="relative flex size-8 shrink-0 items-center gap-2">
                    <div
                        className={clsx(
                            'absolute left-[4px] flex size-8 shrink-0 rotate-12 items-center justify-center rounded-xl bg-neutral-surface text-base text-secondary shadow-subtle transition-all ease-snappy-out',
                            'border border-[var(--neutral-3)]',
                            'group-hover/QAcard:left-[10px] group-hover/QAcard:scale-105 group-hover/QAcard:rotate-[25deg] group-hover/QAcard:bg-base group-hover/QAcard:text-base',
                            'group-focus/QAcard:left-[10px] group-focus/QAcard:scale-105 group-focus/QAcard:rotate-[25deg] group-focus/QAcard:bg-base group-focus/QAcard:text-base'
                        )}
                    >
                        <Icon.GitLab size={18} />
                    </div>

                    <div
                        className={clsx(
                            'absolute -left-[10px] flex size-8 shrink-0 -rotate-12 items-center justify-center rounded-xl bg-neutral-surface text-base text-secondary shadow-subtle transition-all ease-snappy-out group-hover/QAcard:-rotate-12 group-focus/QAcard:-rotate-12',
                            'border border-[var(--neutral-3)]',
                            'group-hover/QAcard:scale-105 group-hover/QAcard:rotate-[-25deg] group-hover/QAcard:bg-base',
                            'group-focus/QAcard:scale-105 group-focus/QAcard:rotate-[-25deg] group-focus/QAcard:bg-base'
                        )}
                    >
                        <Icon.GitHub size={18} />
                    </div>
                    <div />
                </div>
            )}
            <QuickActionCardHeading>Sync with git</QuickActionCardHeading>
        </QuickActionCard>
    );
}
